import { Analytics } from 'components/flowBuilder/types';

export enum AnalyticsTypesNames {
  CONVERSATION_CONVERSION_RATE = 'conversation_conversion_rate',
  CONVERSATIONS = 'conversations',
  CONVERSION_RATE = 'conversion_rate',
  CREDITS_USED = 'credits_used',
  CTR = 'ctr',
  DELIVERABILITY = 'deliverability',
  EPC = 'earnings_per_conversation',
  EPM = 'epm',
  MESSAGE_CONVERSION_RATE = 'mcvr',
  ORDERS = 'orders',
  OUTBOUND = 'outbound',
  PSPAY_ATTRIBUTED_REVENUE = 'pspay_attributed_revenue',
  PSPAY_ORDERS = 'pspay_orders',
  QUEUED_SUBSCRIBERS = 'queued_subscribers',
  REPLY_RATE = 'reply_rate',
  REVENUE = 'revenue',
  TOTAL_CLICKS = 'total_clicks',
  TOTAL_FAILED = 'total_failed',
  TOTAL_SENT = 'total_sent',
  TOTAL_SUBSCRIBERS = 'total_subscribers',
  TOTAL_UNSUBS = 'total_unsubs',
  UCTR = 'uctr',
  UNIQUE_CLICKS = 'unique_clicks',
  UNIQUE_SUBSCRIBERS = 'unique_subscribers',
  UNSUBSCRIBE_RATE = 'unsubscribe_rate',
  UNSUBSCRIBE_RATE_PER_CONVERSATION = 'unsubscribe_rate_per_conversation',
  VALID_MESSAGE_STATISTICS = 'valid_message_statistics',
}

export const StatNames = {
  [AnalyticsTypesNames.CONVERSATION_CONVERSION_RATE]:
    'Conversation Conversion Rate',
  [AnalyticsTypesNames.CONVERSATIONS]: 'Conversations',
  [AnalyticsTypesNames.CONVERSION_RATE]: 'CVR',
  [AnalyticsTypesNames.CTR]: 'CTR',
  [AnalyticsTypesNames.DELIVERABILITY]: 'Deliverability',
  [AnalyticsTypesNames.EPM]: 'EPM',
  [AnalyticsTypesNames.EPC]: 'EPC',
  [AnalyticsTypesNames.MESSAGE_CONVERSION_RATE]: 'Msg conversion rate',
  [AnalyticsTypesNames.ORDERS]: 'Orders',
  [AnalyticsTypesNames.OUTBOUND]: 'Pending',
  [AnalyticsTypesNames.PSPAY_ATTRIBUTED_REVENUE]: 'Text-to-Buy',
  [AnalyticsTypesNames.REPLY_RATE]: 'Reply Rate',
  [AnalyticsTypesNames.REVENUE]: 'Revenue',
  [AnalyticsTypesNames.TOTAL_CLICKS]: 'Clicks',
  [AnalyticsTypesNames.TOTAL_SENT]: 'Messages sent',
  [AnalyticsTypesNames.TOTAL_SUBSCRIBERS]: 'Subscribers',
  [AnalyticsTypesNames.UCTR]: 'UCTR',
  [AnalyticsTypesNames.UNSUBSCRIBE_RATE]: 'Unsubscribe rate',
} as const;

export const StatLongNames = {
  [AnalyticsTypesNames.EPM]: 'Earnings per message',
  [AnalyticsTypesNames.EPC]: 'Earnings per conversation',
  [AnalyticsTypesNames.MESSAGE_CONVERSION_RATE]: 'Message conversion rate',
  [AnalyticsTypesNames.UCTR]: 'Unique click-through rate (UCTR)',
};

export const StatShortNames = {
  [AnalyticsTypesNames.CONVERSATION_CONVERSION_RATE]: 'Conv. Conversion Rate',
  [AnalyticsTypesNames.CONVERSATIONS]: 'Conversations',
  [AnalyticsTypesNames.CONVERSION_RATE]: 'Click conv.',
  [AnalyticsTypesNames.CTR]: 'CTR',
  [AnalyticsTypesNames.DELIVERABILITY]: 'Deliv.',
  [AnalyticsTypesNames.EPM]: 'EPM',
  [AnalyticsTypesNames.EPC]: 'EPC',
  [AnalyticsTypesNames.MESSAGE_CONVERSION_RATE]: 'Msg conv.',
  [AnalyticsTypesNames.ORDERS]: 'Orders',
  [AnalyticsTypesNames.OUTBOUND]: 'Pending',
  [AnalyticsTypesNames.PSPAY_ATTRIBUTED_REVENUE]: 'T2B',
  [AnalyticsTypesNames.REPLY_RATE]: 'Reply rate',
  [AnalyticsTypesNames.REVENUE]: 'Rev.',
  [AnalyticsTypesNames.TOTAL_CLICKS]: 'Clicks',
  [AnalyticsTypesNames.TOTAL_SENT]: 'Sent',
  [AnalyticsTypesNames.TOTAL_SUBSCRIBERS]: 'Subscribers',
  [AnalyticsTypesNames.UCTR]: 'UCTR',
  [AnalyticsTypesNames.UNSUBSCRIBE_RATE]: 'Unsub.',
} as const;

export const AnalyticsGroupsTypesNames = {
  ORDERS: 'ORDERS',
  CONVERSATIONS_AND_REPLY_RATE: 'CONVERSATIONS_AND_REPLY_RATE',
  CVR_AND_CONVERSION_RATE: 'CVR_AND_CONVERSION_RATE',
  ORDERS_AND_CONVERSION_RATE: 'ORDERS_AND_CONVERSION_RATE',
  TOTAL_CLICKS: 'TOTAL_CLICKS',
  CTR_AND_UNIQUE_CTR: 'CTR_AND_UNIQUE_CTR',
  REVENUE_AND_EPM: 'REVENUE_AND_EPM',
  TOTAL_MESSAGES_SENT: 'TOTAL_MESSAGES_SENT',
  TOTAL_CLICKS_AND_CTR: 'TOTAL_CLICKS_AND_CTR',
  TOTAL_CLICKS_AND_CTR_AND_UCTR: 'TOTAL_CLICKS_AND_CTR_AND_UCTR',
  TOTAL_SENT_AND_DELIVERABILITY: 'TOTAL_SENT_AND_DELIVERABILITY',
  TOTAL_SENT_AND_UNSUBSCRIBE_RATE: 'TOTAL_SENT_AND_UNSUBSCRIBE_RATE',
  TOTAL_SUBSCRIBERS_AND_UNSUBSCRIBE_RATE:
    'TOTAL_SUBSCRIBERS_AND_UNSUBSCRIBE_RATE',
} as const;

// List of overall flow analytics displayed in the flows list view
export const FlowsListAnalyticsGroups = [
  AnalyticsGroupsTypesNames.TOTAL_SENT_AND_UNSUBSCRIBE_RATE,
  AnalyticsGroupsTypesNames.TOTAL_CLICKS_AND_CTR,
  AnalyticsGroupsTypesNames.ORDERS_AND_CONVERSION_RATE,
  AnalyticsGroupsTypesNames.REVENUE_AND_EPM,
] as const;

// List of overall flow analytics displayed in the flows list view with Conversation Analytics Enabled
export const FlowsListAnalyticsGroupsWithConversationAnalytics = [
  ...FlowsListAnalyticsGroups,
  AnalyticsGroupsTypesNames.CONVERSATIONS_AND_REPLY_RATE,
];

// List of overall flow analytics displayed in the flow view
export const OverallAnalyticsGroups = [
  AnalyticsGroupsTypesNames.REVENUE_AND_EPM,
  AnalyticsGroupsTypesNames.ORDERS,
  AnalyticsGroupsTypesNames.CVR_AND_CONVERSION_RATE,
  AnalyticsGroupsTypesNames.TOTAL_CLICKS,
  AnalyticsGroupsTypesNames.CTR_AND_UNIQUE_CTR,
  AnalyticsGroupsTypesNames.CONVERSATIONS_AND_REPLY_RATE,
  AnalyticsGroupsTypesNames.TOTAL_MESSAGES_SENT,
  AnalyticsGroupsTypesNames.TOTAL_SUBSCRIBERS_AND_UNSUBSCRIBE_RATE,
] as const;

// List of action analytics displayed in the flow view
export const ActionAnalyticsTypesNames = [
  AnalyticsTypesNames.REVENUE,
  AnalyticsTypesNames.EPM,
  AnalyticsTypesNames.ORDERS,
  AnalyticsTypesNames.CONVERSION_RATE,
  AnalyticsTypesNames.MESSAGE_CONVERSION_RATE,
  AnalyticsTypesNames.TOTAL_CLICKS,
  AnalyticsTypesNames.CTR,
  AnalyticsTypesNames.UCTR,
  AnalyticsTypesNames.TOTAL_SENT,
  AnalyticsTypesNames.UNSUBSCRIBE_RATE,
  AnalyticsTypesNames.REPLY_RATE,
  AnalyticsTypesNames.CONVERSATIONS,
] as const;

export const FlowAnalyticsTypesNames = {
  OVERALL: 'overall',
  OVERALL_SLIM: 'overall-slim',
  OUTBOUND: 'outbound',
  ACTIONS: 'actions',
  MESSAGES: 'messages',
  MESSAGES_SLIM: 'messages-slim',
  CONVERSATIONS: 'conversations',
  CONVERSATIONS_SLIM: 'conversations-slim',
} as const;

export const EMPTY_ANALYTICS: Analytics = {
  [AnalyticsTypesNames.CREDITS_USED]: 0,
  [AnalyticsTypesNames.ORDERS]: 0,
  [AnalyticsTypesNames.REVENUE]: 0,
  [AnalyticsTypesNames.TOTAL_CLICKS]: 0,
  [AnalyticsTypesNames.TOTAL_SENT]: 0,
  [AnalyticsTypesNames.TOTAL_SUBSCRIBERS]: 0,
  [AnalyticsTypesNames.TOTAL_UNSUBS]: 0,
  [AnalyticsTypesNames.UNIQUE_CLICKS]: 0,
  [AnalyticsTypesNames.CONVERSATION_CONVERSION_RATE]: null,
  [AnalyticsTypesNames.UNSUBSCRIBE_RATE_PER_CONVERSATION]: null,
  [AnalyticsTypesNames.OUTBOUND]: 0,
  [AnalyticsTypesNames.TOTAL_FAILED]: 0,
  [AnalyticsTypesNames.VALID_MESSAGE_STATISTICS]: false,
} as const;

export const ANALYTICS_REFETCH_INTERVAL = 1000 * 30; // 30 seconds

export const AnalyticsTabs = {
  TOTAL: 'total',
  MESSAGES: 'messages',
  CONVERSATIONS: 'conversations',
} as const;

export const AnalyticsCategories = {
  OVERALL: 'overall',
  MESSAGES: 'messagesOverall',
  CONVERSATIONS: 'conversationsOverall',
} as const;

export const EMPTY_CATEGORIZED_ANALYTICS = {
  [AnalyticsCategories.OVERALL]: EMPTY_ANALYTICS,
  [AnalyticsCategories.MESSAGES]: EMPTY_ANALYTICS,
  [AnalyticsCategories.CONVERSATIONS]: EMPTY_ANALYTICS,
} as const;
